import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Alert,
  Collapse,
  CircularProgress,
  IconButton,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Support as SupportIcon,
  Assignment as AssignmentIcon,
  Public as PublicIcon,
  AccountBalance as AccountIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Save as SaveIcon,
  ExpandLess,
  ExpandMore,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Cake as CakeIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { SysEditor, SysEditorWhatsapp } from '../../components/syseditor';
import notificationTemplateService, { NotificationTemplate } from '../../services/notificationTemplateService';

interface TemplateData {
  enable_email: boolean;
  enable_sms: boolean;
  enable_whatsapp: boolean;
  custom_sms_message: string;
  custom_email_message: string;
  custom_whatsapp_message: string;
  custom_sms_segundo_envio_msg: string;
  custom_email_segundo_envio_msg: string;
  custom_whatsapp_segundo_envio_msg: string;
  email_subject: string;
  ativo: boolean;
  primeiro_envio: number;
  segundo_envio: number;
  enviar_fatura_pdf: boolean;
}

interface TemplateCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  eventos: {
    id: string;
    label: string;
    evento: string; // Nome do evento no banco (ex: lembrete_antes_vencimento)
    template: TemplateData;
    templateId: number; // ID do template no banco
  }[];
}

interface TemplatesModalProps {
  open: boolean;
  onClose: () => void;
}

const TemplatesModal: React.FC<TemplatesModalProps> = ({ open, onClose }) => {
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>('fatura');
  const [eventoSelecionado, setEventoSelecionado] = useState<string>('');
  const [abaSelecionada, setAbaSelecionada] = useState<number>(0);
  const [categoriasExpanded, setCategoriasExpanded] = useState<Record<string, boolean>>({
    aniversario: false,
    fatura: false, // Primeira categoria expandida por padrão
    chamado: false,
    contrato: false,
    comum: false,
    conta: false,
  });

  // Estados para integração com backend
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [abaEnvioSms, setAbaEnvioSms] = useState<number>(0);
  const [abaEnvioEmail, setAbaEnvioEmail] = useState<number>(0);
  const [abaEnvioWhatsapp, setAbaEnvioWhatsapp] = useState<number>(0);

  // Estrutura de categorias e eventos - será carregada do backend
  const [categorias, setCategorias] = useState<TemplateCategory[]>([]);

  // Estados para templates carregados do backend
  const [templatesCarregados, setTemplatesCarregados] = useState<NotificationTemplate[]>([]);
  const [carregandoTemplates, setCarregandoTemplates] = useState(true);

  // Rastrear quais templates foram modificados (pelo templateId do banco)
  const [templatesModificados, setTemplatesModificados] = useState<Set<number>>(new Set());

  // Buscar categoria e evento atuais
  const categoriaAtual = categorias.find(cat => cat.id === categoriaSelecionada);
  const eventoAtual = categoriaAtual?.eventos.find(evt => evt.id === eventoSelecionado);

  // Carregar todos os templates do backend
  const carregarTemplates = async () => {
    try {
      setCarregandoTemplates(true);
      const templates = await notificationTemplateService.getAll();
      setTemplatesCarregados(templates);

      // Estruturar categorias baseado nos templates reais
      const categoriasEstruturadas = estruturarCategorias(templates);
      setCategorias(categoriasEstruturadas);
      setTemplatesModificados(new Set());

    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setNotification({
        open: true,
        message: 'Erro ao carregar templates do servidor',
        severity: 'error'
      });

      // Auto-fechar mensagem após 6 segundos
      setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 6000);
    } finally {
      setCarregandoTemplates(false);
    }
  };

  // Estruturar templates em categorias
  const estruturarCategorias = (templates: NotificationTemplate[]): TemplateCategory[] => {
    const estruturaBase: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
      aniversario: {
        label: 'Aniversário',
        icon: <CakeIcon fontSize="small" />,
        color: '#1976d2'
      },
      fatura: {
        label: 'Fatura',
        icon: <ReceiptIcon fontSize="small" />,
        color: '#1976d2'
      },
      chamado: {
        label: 'Chamado',
        icon: <SupportIcon fontSize="small" />,
        color: '#d32f2f'
      },
      contrato: {
        label: 'Contrato',
        icon: <AssignmentIcon fontSize="small" />,
        color: '#388e3c'
      },
      comum: {
        label: 'Comum',
        icon: <PublicIcon fontSize="small" />,
        color: '#ff9800'
      },
      conta: {
        label: 'Conta',
        icon: <AccountIcon fontSize="small" />,
        color: '#9c27b0'
      }
    };

    const categoriasMap: Record<string, TemplateCategory> = {};

    // Agrupar templates por categoria
    templates.forEach(template => {
      const categoriaId = template.categoria;

      if (!categoriasMap[categoriaId]) {
        const estrutura = estruturaBase[categoriaId] || {
          label: template.categoria,
          icon: <ReceiptIcon fontSize="small" />,
          color: '#666666'
        };

        categoriasMap[categoriaId] = {
          id: categoriaId,
          label: estrutura.label,
          icon: estrutura.icon,
          color: estrutura.color,
          eventos: []
        };
      }

      // Adicionar evento à categoria
      categoriasMap[categoriaId].eventos.push({
        id: template.id.toString(),
        label: template.nome,
        evento: template.evento,
        templateId: template.id,
        template: {
          enable_email: template.enable_email,
          enable_sms: template.enable_sms,
          enable_whatsapp: template.enable_whatsapp,
          custom_email_message: template.custom_email_message || '',
          custom_sms_message: template.custom_sms_message || '',
          custom_whatsapp_message: template.custom_whatsapp_message || '',
          custom_email_segundo_envio_msg: template.custom_email_segundo_envio_msg || '',
          custom_sms_segundo_envio_msg: template.custom_sms_segundo_envio_msg || '',
          custom_whatsapp_segundo_envio_msg: template.custom_whatsapp_segundo_envio_msg || '',
          email_subject: template.email_subject || '',
          ativo: template.ativo,
          primeiro_envio: template.primeiro_envio || 0,
          segundo_envio: template.segundo_envio || 0,
          enviar_fatura_pdf: template.enviar_fatura_pdf || false
        }
      });
    });

    return Object.values(categoriasMap);
  };

  // Carregar templates na inicialização
  useEffect(() => {
    if (open) {
      carregarTemplates();
    }
  }, [open]);

  // Selecionar primeiro evento quando categoria muda
  useEffect(() => {
    if (categoriaAtual && categoriaAtual.eventos.length > 0) {
      setEventoSelecionado(categoriaAtual.eventos[0].id);
    }
  }, [categoriaSelecionada]);

  const handleUpdateTemplate = (field: keyof TemplateData, value: any) => {
    if (!eventoAtual) return;

    setTemplatesModificados(prev => new Set(prev).add(eventoAtual.templateId));

    setCategorias(prev => prev.map(categoria =>
      categoria.id === categoriaSelecionada
        ? {
          ...categoria,
          eventos: categoria.eventos.map(evt =>
            evt.id === eventoSelecionado
              ? {
                ...evt,
                template: {
                  ...evt.template,
                  [field]: value
                }
              }
              : evt
          )
        }
        : categoria
    ));
  };

  const coletarTodosEventos = (): { templateId: number; template: TemplateData }[] => {
    const eventos: { templateId: number; template: TemplateData }[] = [];
    categorias.forEach(cat => {
      cat.eventos.forEach(evt => {
        if (templatesModificados.has(evt.templateId)) {
          eventos.push({ templateId: evt.templateId, template: evt.template });
        }
      });
    });
    return eventos;
  };

  const handleSalvar = async () => {
    const modificados = coletarTodosEventos();

    if (modificados.length === 0) {
      setNotification({
        open: true,
        message: 'Nenhum template foi alterado.',
        severity: 'info'
      });
      setTimeout(() => setNotification(prev => ({ ...prev, open: false })), 3000);
      return;
    }

    try {
      setSaving(true);

      const promises = modificados.map(({ templateId, template }) =>
        notificationTemplateService.update(templateId, {
          enable_email: template.enable_email,
          enable_sms: template.enable_sms,
          enable_whatsapp: template.enable_whatsapp,
          custom_email_message: template.custom_email_message,
          custom_sms_message: template.custom_sms_message,
          custom_whatsapp_message: template.custom_whatsapp_message,
          custom_email_segundo_envio_msg: template.custom_email_segundo_envio_msg,
          custom_sms_segundo_envio_msg: template.custom_sms_segundo_envio_msg,
          custom_whatsapp_segundo_envio_msg: template.custom_whatsapp_segundo_envio_msg,
          email_subject: template.email_subject,
          ativo: template.ativo,
          primeiro_envio: template.primeiro_envio,
          segundo_envio: template.segundo_envio,
          enviar_fatura_pdf: template.enviar_fatura_pdf
        })
      );

      await Promise.all(promises);

      setNotification({
        open: true,
        message: `${modificados.length} template${modificados.length > 1 ? 's' : ''} salvo${modificados.length > 1 ? 's' : ''} com sucesso!`,
        severity: 'success'
      });

      setTimeout(() => setNotification(prev => ({ ...prev, open: false })), 4000);

      await carregarTemplates();
    } catch (error: any) {
      console.error('Erro ao salvar templates:', error);
      const backendMessage = error?.response?.data?.message;
      setNotification({
        open: true,
        message: backendMessage || 'Erro ao salvar templates. Tente novamente.',
        severity: 'error'
      });

      setTimeout(() => setNotification(prev => ({ ...prev, open: false })), 6000);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCategoria = (categoriaId: string) => {
    // Fechar todas as outras categorias e expandir apenas a selecionada
    const novoEstado: Record<string, boolean> = {
      aniversario: false,
      fatura: false,
      chamado: false,
      contrato: false,
      comum: false,
      conta: false,
    };

    // Se a categoria já estava expandida, não expandir nenhuma
    // Se estava fechada, expandir apenas ela
    if (!categoriasExpanded[categoriaId]) {
      novoEstado[categoriaId] = true;
    }

    setCategoriasExpanded(novoEstado);
  };

  const handleSelectEvento = (categoriaId: string, eventoId: string) => {
    setCategoriaSelecionada(categoriaId);
    setEventoSelecionado(eventoId);
  };

  const renderConteudoAba = () => {
    if (!eventoAtual) return null;

    const template = eventoAtual.template;

    switch (abaSelecionada) {
      case 0: // SMS
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={template.enable_sms}
                  onChange={(e) => handleUpdateTemplate('enable_sms', e.target.checked)}
                />
              }
              label="Habilitar SMS"
              sx={{ mb: 2 }}
            />

            {(eventoAtual.evento === 'lembrete_antes_vencimento' || eventoAtual.evento === 'lembrete_apos_vencimento') ? (
              <>
                <Tabs
                  value={abaEnvioSms}
                  onChange={(_, v) => setAbaEnvioSms(v)}
                  sx={{ mb: 2, minHeight: 40 }}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Primeiro envio" sx={{ py: 1, minHeight: 40 }} />
                  <Tab label="Segundo envio" sx={{ py: 1, minHeight: 40 }} />
                </Tabs>

                {abaEnvioSms === 0 ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Mensagem SMS (1º Envio)"
                    value={template.custom_sms_message}
                    onChange={(e) => handleUpdateTemplate('custom_sms_message', e.target.value)}
                    disabled={!template.enable_sms}
                    helperText="Mantenha a mensagem concisa (máximo 160 caracteres recomendado)"
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Mensagem SMS (2º Envio)"
                    value={template.custom_sms_segundo_envio_msg}
                    onChange={(e) => handleUpdateTemplate('custom_sms_segundo_envio_msg', e.target.value)}
                    disabled={!template.enable_sms}
                    helperText="Mantenha a mensagem concisa (máximo 160 caracteres recomendado)"
                  />
                )}
              </>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Mensagem SMS"
                value={template.custom_sms_message}
                onChange={(e) => handleUpdateTemplate('custom_sms_message', e.target.value)}
                disabled={!template.enable_sms}
                helperText="Mantenha a mensagem concisa (máximo 160 caracteres recomendado)"
              />
            )}
          </Box>
        );

      case 1: // Email
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={template.enable_email}
                  onChange={(e) => handleUpdateTemplate('enable_email', e.target.checked)}
                />
              }
              label="Habilitar Email"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Assunto do Email"
              value={template.email_subject}
              onChange={(e) => handleUpdateTemplate('email_subject', e.target.value)}
              disabled={!template.enable_email}
              sx={{ mb: 2 }}
            />

            {(eventoAtual.evento === 'lembrete_antes_vencimento' || eventoAtual.evento === 'lembrete_apos_vencimento') ? (
              <>
                <Tabs
                  value={abaEnvioEmail}
                  onChange={(_, v) => setAbaEnvioEmail(v)}
                  sx={{ mb: 2, minHeight: 40 }}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Primeiro envio" sx={{ py: 1, minHeight: 40 }} />
                  <Tab label="Segundo envio" sx={{ py: 1, minHeight: 40 }} />
                </Tabs>

                {abaEnvioEmail === 0 ? (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Corpo do Email (1º Envio)
                    </Typography>
                    <SysEditor
                      value={template.custom_email_message}
                      onChange={(value) => handleUpdateTemplate('custom_email_message', value)}
                      disabled={!template.enable_email}
                      placeholder="Digite o conteúdo do email..."
                      height={300}
                    />
                  </Box>
                ) : (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Corpo do Email (2º Envio)
                    </Typography>
                    <SysEditor
                      value={template.custom_email_segundo_envio_msg}
                      onChange={(value) => handleUpdateTemplate('custom_email_segundo_envio_msg', value)}
                      disabled={!template.enable_email}
                      placeholder="Digite o conteúdo do email..."
                      height={300}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Corpo do Email
                </Typography>
                <SysEditor
                  value={template.custom_email_message}
                  onChange={(value) => handleUpdateTemplate('custom_email_message', value)}
                  disabled={!template.enable_email}
                  placeholder="Digite o conteúdo do email..."
                  height={300}
                />
              </Box>
            )}
          </Box>
        );

      case 2: // WhatsApp
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={template.enable_whatsapp}
                  onChange={(e) => handleUpdateTemplate('enable_whatsapp', e.target.checked)}
                />
              }
              label="Habilitar WhatsApp"
              sx={{ mb: 2 }}
            />

            {(eventoAtual.evento === 'lembrete_antes_vencimento' || eventoAtual.evento === 'lembrete_apos_vencimento') ? (
              <>
                <Tabs
                  value={abaEnvioWhatsapp}
                  onChange={(_, v) => setAbaEnvioWhatsapp(v)}
                  sx={{ mb: 2, minHeight: 40 }}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Primeiro envio" sx={{ py: 1, minHeight: 40 }} />
                  <Tab label="Segundo envio" sx={{ py: 1, minHeight: 40 }} />
                </Tabs>

                {abaEnvioWhatsapp === 0 ? (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mensagem WhatsApp (1º Envio)
                    </Typography>
                    <SysEditorWhatsapp
                      value={template.custom_whatsapp_message}
                      onChange={(value) => handleUpdateTemplate('custom_whatsapp_message', value)}
                      disabled={!template.enable_whatsapp}
                      placeholder="Digite a mensagem do WhatsApp..."
                      rows={10}
                    />
                  </Box>
                ) : (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mensagem WhatsApp (2º Envio)
                    </Typography>
                    <SysEditorWhatsapp
                      value={template.custom_whatsapp_segundo_envio_msg}
                      onChange={(value) => handleUpdateTemplate('custom_whatsapp_segundo_envio_msg', value)}
                      disabled={!template.enable_whatsapp}
                      placeholder="Digite a mensagem do WhatsApp..."
                      rows={10}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Mensagem WhatsApp
                </Typography>
                <SysEditorWhatsapp
                  value={template.custom_whatsapp_message}
                  onChange={(value) => handleUpdateTemplate('custom_whatsapp_message', value)}
                  disabled={!template.enable_whatsapp}
                  placeholder="Digite a mensagem do WhatsApp..."
                  rows={10}
                />
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5">
            Templates de Notificações
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure as mensagens que serão enviadas para os clientes em diferentes situações
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Mensagens de Feedback */}
        {notification.open && (
          <Alert
            severity={notification.severity}
            sx={{ m: 3, mb: 2 }}
            onClose={() => setNotification({ ...notification, open: false })}
          >
            {notification.message}
          </Alert>
        )}

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Menu Lateral */}
            <Grid item xs={12} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Categorias
                  </Typography>

                  {carregandoTemplates ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : categorias.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Nenhum template encontrado
                    </Alert>
                  ) : (
                    <List dense disablePadding>
                      {categorias.map((categoria) => (
                        <React.Fragment key={categoria.id}>
                          <ListItem disablePadding>
                            <ListItemButton
                              onClick={() => handleToggleCategoria(categoria.id)}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                '&:hover': {
                                  backgroundColor: `${categoria.color}10`,
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: categoria.color }}>
                                {categoria.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={categoria.label}
                                primaryTypographyProps={{
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                }}
                              />
                              {categoriasExpanded[categoria.id] ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                          </ListItem>

                          <Collapse in={categoriasExpanded[categoria.id]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {categoria.eventos.map((evento) => (
                                <ListItem key={evento.id} disablePadding>
                                  <ListItemButton
                                    selected={categoriaSelecionada === categoria.id && eventoSelecionado === evento.id}
                                    onClick={() => handleSelectEvento(categoria.id, evento.id)}
                                    sx={{
                                      pl: 4,
                                      py: 0.5,
                                      borderRadius: 1,
                                      mx: 0.5,
                                      '&.Mui-selected': {
                                        backgroundColor: `${categoria.color}15`,
                                        '&:hover': {
                                          backgroundColor: `${categoria.color}20`,
                                        },
                                      },
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 24, mr: 1 }}>
                                      {evento.template.ativo ? (
                                        <CheckCircleIcon
                                          sx={{
                                            fontSize: 16,
                                            color: 'success.main'
                                          }}
                                        />
                                      ) : (
                                        <CancelIcon
                                          sx={{
                                            fontSize: 16,
                                            color: 'error.main'
                                          }}
                                        />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={evento.label}
                                      primaryTypographyProps={{
                                        fontSize: '0.8rem',
                                        color: categoriaSelecionada === categoria.id && eventoSelecionado === evento.id
                                          ? categoria.color
                                          : evento.template.ativo
                                            ? 'text.primary'
                                            : 'text.disabled'
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Conteúdo Principal */}
            <Grid item xs={12} md={9}>
              <Card elevation={2}>
                <CardContent>
                  {carregandoTemplates ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Carregando templates...
                        </Typography>
                      </Box>
                    </Box>
                  ) : eventoAtual ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          {eventoAtual.label}
                          {templatesModificados.has(eventoAtual.templateId) && (
                            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                              (modificado)
                            </Typography>
                          )}
                        </Typography>
                      </Box>

                      {/* Status do Template */}
                      <Box sx={{
                        display: 'flex',

                      }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={eventoAtual.template.ativo}
                              onChange={(e) => handleUpdateTemplate('ativo', e.target.checked)}
                              color="primary"
                              size="medium"
                            />
                          }
                          label={
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              color={eventoAtual.template.ativo ? 'success.dark' : 'warning.dark'}
                            >
                              Ativar Notificação
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>

                      {!eventoAtual.template.ativo && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Este template está desativado. Ative-o para que as notificações sejam enviadas.
                        </Alert>
                      )}

                      {/* Campos de Dias de Envio - Apenas para templates de lembrete */}
                      {(eventoAtual.evento === 'lembrete_antes_vencimento' || eventoAtual.evento === 'lembrete_apos_vencimento') && eventoAtual.template.ativo && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom color="primary">
                            Configuração de Dias para Envio
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Configure quantos dias {eventoAtual.evento === 'lembrete_antes_vencimento' ? 'antes' : 'após'} o vencimento cada envio deve ser realizado. Use 0 para desabilitar.
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                type="number"
                                label="1º Envio (dias)"
                                value={eventoAtual.template.primeiro_envio}
                                onChange={(e) => handleUpdateTemplate('primeiro_envio', Math.max(0, parseInt(e.target.value) || 0))}
                                inputProps={{ min: 0, max: 365 }}
                                helperText="0 = desabilitado"
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                type="number"
                                label="2º Envio (dias)"
                                value={eventoAtual.template.segundo_envio}
                                onChange={(e) => handleUpdateTemplate('segundo_envio', Math.max(0, parseInt(e.target.value) || 0))}
                                inputProps={{ min: 0, max: 365 }}
                                helperText="0 = desabilitado"
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* Checkbox Enviar Fatura em PDF - Apenas para eventos de lembrete e fatura_criada */}
                      {eventoAtual.template.ativo && [
                        'lembrete_antes_vencimento',
                        'lembrete_apos_vencimento',
                        'lembrete_dia_vencimento',
                        'fatura_criada'
                      ].includes(eventoAtual.evento) && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={eventoAtual.template.enviar_fatura_pdf}
                                  onChange={(e) => handleUpdateTemplate('enviar_fatura_pdf', e.target.checked)}
                                  color="primary"
                                  icon={<PictureAsPdfIcon sx={{ color: 'text.disabled' }} />}
                                  checkedIcon={<PictureAsPdfIcon sx={{ color: 'error.main' }} />}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body1" fontWeight="medium">
                                    Enviar Fatura em PDF
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Anexa o PDF da fatura ao envio por WhatsApp e Email (não se aplica a SMS)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        )}

                      {/* Abas de Configuração - Apenas se o template estiver ativo */}
                      {eventoAtual.template.ativo && (
                        <>
                          <Grid item xs={12} sx={{ mt: 3 }}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>

                          <Tabs
                            value={abaSelecionada}
                            onChange={(_, newValue) => setAbaSelecionada(newValue)}
                            sx={{ mb: 3 }}
                          >
                            <Tab
                              icon={<SmsIcon />}
                              label="SMS"
                              iconPosition="start"
                              sx={{ minHeight: 48 }}
                            />
                            <Tab
                              icon={<EmailIcon />}
                              label="Email"
                              iconPosition="start"
                              sx={{ minHeight: 48 }}
                            />
                            <Tab
                              icon={<WhatsAppIcon />}
                              label="WhatsApp"
                              iconPosition="start"
                              sx={{ minHeight: 48 }}
                            />
                          </Tabs>

                          {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            renderConteudoAba()
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">
                      Selecione uma categoria e evento para configurar o template
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {templatesModificados.size > 0 && (
            <Typography variant="body2" color="warning.main">
              {templatesModificados.size} template{templatesModificados.size > 1 ? 's' : ''} com alterações não salvas
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || templatesModificados.size === 0}
          >
            {saving ? 'Salvando...' : `Salvar${templatesModificados.size > 0 ? ` (${templatesModificados.size})` : ''}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatesModal; 